import React, { useCallback, useEffect, useRef, useState } from "react";
import { CONTENT_WIDTHS, BLOCK_SPACING } from "../../lib/wordpress/constants";
import wordpressStyles from "../../styles/wordpress-blocks.css?raw";
import { debounce } from "../../lib/utils";

const PREVIEW_CONFIG = {
  PADDING: 24, // Padding around the preview
  WIDTH: CONTENT_WIDTHS.DEFAULT, // Default content width
  DEBOUNCE: 100, // Debounce delay for resize events
  MIN_HEIGHT: 400, // Minimum iframe height
  MIN_SCALE: 0.1, // Minimum scale factor
  MAX_SCALE: 1, // Maximum scale factor
  RESIZE_OBSERVER_OPTIONS: {
    box: "border-box", // Include padding in size calculations
  } as const,
} as const;

function generateIframeContent(pattern: string): string {
  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <title>Block Pattern Preview</title>
      <base target="_blank">
      <link
        rel="stylesheet"
        href="https://cdn.jsdelivr.net/npm/@wordpress/block-library@latest/build/style.min.css"
      />
      <style>
        ${wordpressStyles}

        body {
          margin: 0;
          padding: 0;
          width: ${PREVIEW_CONFIG.WIDTH}px;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
          margin: 0 auto !important;
          background: #fff;
          overflow-x: hidden;
        }

        img {
          display: block;
          max-width: 100%;
          height: auto;
        }
      </style>
    </head>
    <body>
      ${pattern}
      <script>
        (function() {
          try {
            // Create ResizeObserver to monitor body size changes
            const observer = new ResizeObserver(entries => {
              const entry = entries[0];
              const height = entry.borderBoxSize?.[0]?.blockSize ||
                            entry.target.getBoundingClientRect().height;
              
              window.parent.postMessage({ 
                type: 'preview-resize',
                height: Math.ceil(height)
              }, '*');
            });
            
            // Start observing body size changes
            observer.observe(document.body, ${JSON.stringify(
              PREVIEW_CONFIG.RESIZE_OBSERVER_OPTIONS
            )});
            
            // Handle image loads
            const images = document.getElementsByTagName('img');
            Array.from(images).forEach(img => {
              if (!img.complete) {
                img.addEventListener('load', () => {
                  observer.unobserve(document.body);
                  observer.observe(document.body, ${JSON.stringify(
                    PREVIEW_CONFIG.RESIZE_OBSERVER_OPTIONS
                  )});
                });
              }
            });
          } catch (e) {
            console.error('Preview initialization error:', e);
          }
        })();
      </script>
    </body>
    </html>
  `;
}

interface BlockPatternPreviewProps {
  pattern: string;
}

export function BlockPatternPreview({ pattern }: BlockPatternPreviewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [unscaledHeight, setUnscaledHeight] = useState(
    PREVIEW_CONFIG.MIN_HEIGHT
  );
  const [scale, setScale] = useState(PREVIEW_CONFIG.MAX_SCALE);
  const [width, setWidth] = useState(PREVIEW_CONFIG.WIDTH);
  const resizeTimeoutRef = useRef<number>();

  // Handle messages from the iframe
  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Only accept messages from our preview iframe
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }

      if (event.data?.type === "preview-resize") {
        if (resizeTimeoutRef.current) {
          window.clearTimeout(resizeTimeoutRef.current);
        }
        resizeTimeoutRef.current = window.setTimeout(() => {
          setUnscaledHeight(
            Math.max(event.data.height, PREVIEW_CONFIG.MIN_HEIGHT)
          );
        }, PREVIEW_CONFIG.DEBOUNCE);
      }
    },
    [iframeRef]
  );

  useEffect(() => {
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [handleMessage]);

  // Update scale when container size changes
  useEffect(() => {
    const updateScale = debounce(() => {
      if (!containerRef.current) return;
      const containerWidth =
        containerRef.current.offsetWidth - PREVIEW_CONFIG.PADDING * 2;
      const newScale = Math.min(
        PREVIEW_CONFIG.MAX_SCALE,
        Math.max(
          PREVIEW_CONFIG.MIN_SCALE,
          containerWidth / PREVIEW_CONFIG.WIDTH
        )
      );
      setScale(newScale);
    }, PREVIEW_CONFIG.DEBOUNCE);

    window.addEventListener("resize", updateScale);
    updateScale(); // Initial calculation

    return () => window.removeEventListener("resize", updateScale);
  }, []);

  if (!pattern) {
    return (
      <div className="flex items-center justify-center h-full bg-volt-carbon/50 border-2 border-dashed border-white/10 rounded-lg">
        <p className="text-gray-400">Generate a pattern to see the preview</p>
      </div>
    );
  }

  const scaledHeight = unscaledHeight * scale;

  return (
    <div
      ref={containerRef}
      className="relative bg-white h-full w-full overflow-auto flex-1"
      style={{ minHeight: scaledHeight }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: `translate(-50%, 0) scale(${scale})`,
          transformOrigin: "top center",
          width: `${PREVIEW_CONFIG.WIDTH}px`,
          height: `${unscaledHeight}px`,
          overflow: "visible",
        }}
      >
        <iframe
          ref={iframeRef}
          style={{
            width: `${PREVIEW_CONFIG.WIDTH}px`,
            height: `${unscaledHeight}px`,
            padding: `${PREVIEW_CONFIG.PADDING}px`,
          }}
          className="border-0"
          srcDoc={generateIframeContent(pattern)}
          sandbox="allow-scripts"
          title="Block Pattern Preview"
        />
      </div>
    </div>
  );
}
